import { useAuth0 } from "@auth0/auth0-react";
import {
  Alert,
  alertClasses,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { AutocompleteField, DatePickerField, SelectField, TextAreaField } from "components/UI/Form";
import { Field, Form, Formik } from "formik";
import { type ChangeEvent, type FC, useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import usePermissions from "src/components/CustomHooks/usePermissions";
import {
  PermissionLevel,
  StagelessAction,
  WorkPacketFilter,
  type WorkPacketFilters,
  type WorkPacketOwner,
} from "src/types/work-packets";
import { EMPTY_ARRAY } from "src/utils/empty-values";
import { bulkUpdateWorkPackets, type BulkUpdateWorkPacketsParams, type UpdatableValues } from "../api/workPacketsAPI";
import { useUserOptions } from "../useUserOptions";
import { useDialogActionStore } from "../WorkPacketDetailsPopup/DialogActionContext";
import { FileUploadBox } from "../WorkPacketDetailsPopup/Summary/components/FileUploadBox";
import { FileUploadContext, type FileUploadStatus } from "../WorkPacketDetailsPopup/Summary/FileUploadContext";
import { useFileUploadStore } from "../WorkPacketDetailsPopup/Summary/useFileUploadStore";
import { useWorkPacketsContext } from "../WorkPacketsContext";
import { WorkPacketType } from "../WorkPacketType";
import { getEditWorkPacketActionOptions } from "./helpers/getStaticFilterOptions";
import { WORK_PACKET_SUPERVISOR_PERMISSIONS } from "src/pages/UserDashboard/WorkPackets/WorkPacketPermission";
import {
  abandonedReasonOptions,
  type AbandonedReasonOption,
  deniedReasonOptions,
  type DeniedReasonOption,
} from "../../Cases/api/constants";
import { getStagelessActionValue } from "../mappers";
import * as Yup from "yup";

/** How many work packets can be selected for bulk actions before warning the user. */
const WARNING_THRESHOLD = 500;

interface BulkUpdatePopup {
  workPacketIds?: string[];
  excludeIds?: string[];
  filters: WorkPacketFilters;
  selectedAll?: boolean;
  open: boolean;
  allowEditPacketActionAndNotes: boolean;
  onClose: (success?: boolean) => void;
  effectiveSelectionCount: number;
}

const bulkUpdateMutationOptions: UseMutationOptions<void, Error, BulkUpdateWorkPacketsParams> = {
  mutationFn: bulkUpdateWorkPackets,
  mutationKey: ["work-packets-bulk-update"],
  onError: err => {
    toast.error("Error updating work packets");
    console.error(err);
  },
};

const numberFormatter = new Intl.NumberFormat();

const VALID_THRESHOLD_CONFIRMATION_REGEX = /^[0-9,]*$/;

const validationSchema = Yup.object().shape({
  [WorkPacketFilter.CurrentAction]: Yup.string().optional(),
  abandonedReason: Yup.string().when([WorkPacketFilter.CurrentAction], {
    is: (currentAction: string) => currentAction === getStagelessActionValue(StagelessAction.Abandoned),
    then: schema =>
      schema.required("Abandoned reason is required").oneOf(
        abandonedReasonOptions.map(option => option.value),
        "Please select a valid abandoned reason",
      ),
    otherwise: schema => schema.oneOf([""], "Abandoned reason cannot be set unless action is Abandoned"),
  }),
});

export const BulkUpdatePopup: FC<BulkUpdatePopup> = ({
  workPacketIds,
  excludeIds,
  open,
  filters,
  selectedAll,
  allowEditPacketActionAndNotes,
  onClose,
  effectiveSelectionCount,
}) => {
  const { user } = useAuth0();
  const { currentWorkPacketType, currentFilters } = useWorkPacketsContext();
  const queryClient = useQueryClient();

  const { isAdmin, hasPermission } = usePermissions();
  const isPreCaseView = !!currentFilters[WorkPacketFilter.PreCase];
  const isSupervisor = hasPermission(WORK_PACKET_SUPERVISOR_PERMISSIONS[currentWorkPacketType]);

  const permissionLevel = isAdmin
    ? PermissionLevel.Admin
    : isSupervisor
      ? PermissionLevel.Supervisor
      : PermissionLevel.Default;

  const actionOptions = getEditWorkPacketActionOptions(currentWorkPacketType, isPreCaseView, permissionLevel);

  const dialogActionStore = useDialogActionStore();

  const initialValues: UpdatableValues = {
    [WorkPacketFilter.CurrentAction]: "",
    [WorkPacketFilter.CurrentPacketOwner]: "",
    [WorkPacketFilter.BackupPacketOwner]: "",
    [WorkPacketFilter.MonitoringPacketOwner]: "",
    isValidWorkPacket: undefined,
    notes: "",
    monitoringDate: undefined,
    deniedReason: "",
    abandonedReason: "",
  };

  const bulkUpdateMutation = useMutation(bulkUpdateMutationOptions);
  const loading = bulkUpdateMutation.isPending;

  const fileUploadStore = useFileUploadStore({
    dialogActionStore,
    existingFiles: EMPTY_ARRAY,
    isLoadingExistingFiles: false,
    isUploading: bulkUpdateMutation.isPending,
  });

  const setUploadStatus = useCallback(
    (uploadStatus: FileUploadStatus) => {
      for (const fileUpload of fileUploadStore.fileUploads) {
        fileUploadStore.setFileUploadStatus(fileUpload.file.name, uploadStatus);
      }
    },
    [fileUploadStore],
  );

  const onFormSubmit = async (fields: typeof initialValues) => {
    if (!user?.sub) return;
    await bulkUpdateMutation
      .mutateAsync({
        workPacketType: currentWorkPacketType,
        workPacketIds: selectedAll ? [] : workPacketIds || [],
        excludeIds: selectedAll ? excludeIds || [] : [],
        filters,
        fields,
        files: fileUploadStore.fileUploads.map(u => u.file),
        onUploadProgress: uploadProgress => setUploadStatus({ status: "uploading", uploadProgress }),
      })
      .then(() => setUploadStatus({ status: "uploaded" }))
      .catch(error => setUploadStatus({ status: "failed", error }));
    queryClient.invalidateQueries({ queryKey: ["work-packets", currentWorkPacketType] });
    onClose(true); // `true` invalidates work packet list data
  };

  const currentOwnerOptions = useUserOptions(isSupervisor);

  /**
   * DNA-3343 Setting showDeniedReasonField to false as we are removing configCat feature flags and this is
   * marked false by default
   */
  const showDeniedReasonField = false;

  const exceedsWarningThreshold = effectiveSelectionCount >= WARNING_THRESHOLD;

  const [thresholdConfirmationInput, setThresholdConfirmationInput] = useState("");
  const onThresholdConfirmationInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (VALID_THRESHOLD_CONFIRMATION_REGEX.test(value)) {
      setThresholdConfirmationInput(value);
    }
  };

  const isDisabledByThreshold =
    exceedsWarningThreshold && thresholdConfirmationInput.replaceAll(",", "") !== effectiveSelectionCount.toString();

  useEffect(() => {
    if (!open) {
      setThresholdConfirmationInput("");
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Actions</DialogTitle>

      <Formik initialValues={initialValues} onSubmit={onFormSubmit} validationSchema={validationSchema}>
        {({ setFieldValue, dirty, values, isValid }) => (
          <Form>
            <DialogContent>
              <Divider />

              <Box display="flex" flexDirection="column" mb={4} gap={2}>
                {exceedsWarningThreshold && (
                  <Alert severity="warning" sx={{ [`& .${alertClasses.message}`]: { width: "100%" } }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "1.5rem" }}>
                        <p>Heads up! You are targetting</p>
                        <p style={{ fontSize: "4rem", lineHeight: 1.2 }}>
                          {numberFormatter.format(effectiveSelectionCount)}
                        </p>
                        <p>work packets in your current selection.</p>
                        <p>Please ensure your filters are correct.</p>
                      </div>
                      <hr />
                      <div>
                        <p>
                          To confirm this action, please type the number of affected work packets (
                          <code
                            style={{
                              color: "#DD0000",
                              backgroundColor: "#FFF0F0",
                              padding: "2px 4px",
                              borderRadius: "4px",
                            }}
                          >
                            {effectiveSelectionCount}
                          </code>
                          ) in the input below:
                        </p>
                        <TextField
                          type="text"
                          value={thresholdConfirmationInput}
                          onChange={onThresholdConfirmationInputChange}
                          sx={{ paddingBlock: "2px" }}
                          inputProps={{ style: { padding: "4px 4px", backgroundColor: "white", textAlign: "center" } }}
                        />
                      </div>
                    </div>
                  </Alert>
                )}
                <Box
                  display="grid"
                  sx={{
                    gridTemplateColumns: {
                      xs: "repeat(1, 1fr)",
                      sm: "repeat(2, 1fr)",
                    },
                    gap: 2,
                    rowGap: 3,
                  }}
                >
                  {actionOptions && (
                    <Field
                      name={WorkPacketFilter.CurrentAction}
                      component={AutocompleteField}
                      label="Current Action"
                      options={actionOptions}
                      groupBy={(option: any) => option.category}
                      getOptionLabel={(option: any) => option.title}
                      placeholder="Current Action"
                      setFieldValue={setFieldValue}
                      disabled={loading || !allowEditPacketActionAndNotes}
                    />
                  )}

                  {currentWorkPacketType === WorkPacketType.SHORTAGES && isPreCaseView && showDeniedReasonField && (
                    <Field
                      name="deniedReason"
                      component={AutocompleteField}
                      label="Denied Reason"
                      options={deniedReasonOptions}
                      getOptionLabel={(option: DeniedReasonOption) => option.title}
                      placeholder="Denied Reason"
                      setFieldValue={setFieldValue}
                      disabled={loading}
                    />
                  )}

                  <Field
                    name={WorkPacketFilter.CurrentPacketOwner}
                    component={AutocompleteField}
                    label={
                      currentWorkPacketType === WorkPacketType.ACCRUALS
                        ? "Disputing Work Packet Owner"
                        : "Current Packet Owner"
                    }
                    options={currentOwnerOptions}
                    getOptionLabel={(option: WorkPacketOwner) => option.title}
                    placeholder={
                      currentWorkPacketType === WorkPacketType.ACCRUALS
                        ? "Disputing Work Packet Owner"
                        : "Current Packet Owner"
                    }
                    setFieldValue={setFieldValue}
                    disabled={loading}
                  />

                  <Field
                    name={WorkPacketFilter.BackupPacketOwner}
                    component={AutocompleteField}
                    label="Backup Packet Owner"
                    options={currentOwnerOptions}
                    getOptionLabel={(option: WorkPacketOwner) => option.title}
                    placeholder="Backup Packet Owner"
                    setFieldValue={setFieldValue}
                    disabled={loading}
                  />

                  {currentWorkPacketType === WorkPacketType.SHORTAGES && isPreCaseView && (
                    <Field
                      name="abandonedReason"
                      component={AutocompleteField}
                      label="Abandoned Reason"
                      options={abandonedReasonOptions}
                      getOptionLabel={(option: AbandonedReasonOption) => option.title}
                      placeholder="Abandoned Reason"
                      setFieldValue={setFieldValue}
                      disabled={
                        loading ||
                        (values.currentAction !== getStagelessActionValue(StagelessAction.Abandoned) &&
                          values.abandonedReason === "")
                      }
                    />
                  )}

                  {currentWorkPacketType === WorkPacketType.ACCRUALS && (
                    <Field
                      name={WorkPacketFilter.MonitoringPacketOwner}
                      component={AutocompleteField}
                      label="Monitoring Packet Owner"
                      options={currentOwnerOptions}
                      getOptionLabel={(option: WorkPacketOwner) => option.title}
                      placeholder="Monitoring Packet Owner"
                      setFieldValue={setFieldValue}
                      disabled={loading}
                    />
                  )}

                  {currentWorkPacketType === WorkPacketType.CHARGEBACKS && (
                    <Field
                      name="isValidWorkPacket"
                      value
                      component={SelectField}
                      label="Is Valid Chargeback?"
                      options={[
                        { value: null, label: "(unchanged)" },
                        { value: true, label: "Yes" },
                        { value: false, label: "No" },
                      ]}
                    />
                  )}

                  {currentWorkPacketType === WorkPacketType.ACCRUALS && (
                    <Field
                      name={WorkPacketFilter.ConsolidatedDisputeId}
                      as={TextField}
                      variant="outlined"
                      label="Consolidated Dispute ID"
                      placeholder="Enter Consolidated Dispute ID"
                      size="small"
                      fullWidth
                    />
                  )}

                  {currentWorkPacketType === WorkPacketType.ACCRUALS && (
                    <Field
                      name={WorkPacketFilter.OriginalDisputeId}
                      as={TextField}
                      variant="outlined"
                      label="Original Dispute ID"
                      placeholder="Enter Original Dispute ID"
                      size="small"
                      fullWidth
                    />
                  )}
                  {currentWorkPacketType === WorkPacketType.ACCRUALS && (
                    <Field
                      name="monitoringDate"
                      component={DatePickerField}
                      variant="outlined"
                      label="Monitoring Date"
                      placeholder="Monitoring Date"
                      size="small"
                    />
                  )}
                  {currentWorkPacketType === WorkPacketType.ACCRUALS && (
                    <Field
                      name="disputeCreatedDate"
                      component={DatePickerField}
                      variant="outlined"
                      label="Dispute Created Date"
                      placeholder="Dispute Created Date"
                      size="small"
                    />
                  )}
                </Box>

                <Field
                  name="notes"
                  component={TextAreaField}
                  label="Notes"
                  disabled={loading || !allowEditPacketActionAndNotes}
                />

                {currentWorkPacketType === WorkPacketType.ACCRUALS && (
                  <Box>
                    <Typography fontWeight={500} mb={2}>
                      Evidence Attachments
                    </Typography>
                    <FileUploadContext.Provider value={fileUploadStore}>
                      <FileUploadBox
                        accept=".doc, .docx, .xls, .xlsx, .xlsv, .csv, .png, .jpg, .jpeg"
                        acceptHint="DOC(X), XLS(X), CSV, PDF, PNG, JPG"
                      />
                    </FileUploadContext.Provider>
                  </Box>
                )}
              </Box>
            </DialogContent>

            <DialogActions sx={{ backgroundColor: "transparent", borderTop: "1px solid #E4E4E7" }}>
              <Button className="contained-reversed" variant="contained" onClick={() => onClose()} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!(dirty || fileUploadStore.hasPendingUploads) || loading || isDisabledByThreshold || !isValid}
                sx={{ paddingX: "24px" }}
              >
                <span>{isDisabledByThreshold ? <>Acknowledge warning to proceed</> : <>Save Changes</>}</span>
                {loading && <CircularProgress size={14} style={{ marginLeft: "12px", color: "#101828" }} />}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
